import { useEffect } from 'react';
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
} from '@tanstack/react-router';

import { GapDraftReviewStatus } from '@momentum/types';

import '@/features/gap-review/gap-review.css';
import { GapReviewTopbar } from '@/features/gap-review/components/GapReviewTopbar';
import { GapReviewDataProvider } from '@/features/gap-review/gap-review-context';
import { useGapReviewTheme } from '@/features/gap-review/hooks/useGapReviewTheme';
import { countKeptAcrossRuns } from '@/features/gap-review/lib/final-review';
import { resolveSelectedRun } from '@/features/gap-review/lib/selected-run';
import { useGapDrafts, useGapExecutions } from '@/shared/hooks/useGapReview';
import { useInternalAccess } from '@/shared/hooks/useInternalAccess';
import { PlatformRestriction } from '@/shared/navigation/components/PlatformRestriction';

/** Exported so `routeTree.gen.ts` can name it under declaration emit. */
export interface GapReviewLayoutSearch {
  /**
   * Which run's gaps are loaded. Declared here rather than on the queue route so
   * every view below inherits it — the run is the one choice they all share.
   *
   * Optional in the URL and never optional in effect: a URL that names no run is
   * answered by selecting the most recent one and writing it back in.
   */
  execution?: string;
}

export const Route = createFileRoute('/_dashboard/gap-review')({
  component: GapReviewRoute,
  validateSearch: (search: Record<string, unknown>): GapReviewLayoutSearch => ({
    execution:
      typeof search.execution === 'string' ? search.execution : undefined,
  }),
});

/**
 * The door, for every gap-review view: the four below are nested under this
 * route, so turning someone away here turns them away from all of them.
 *
 * Anyone else is sent home rather than shown a refusal — this is an internal
 * tool and a "you may not see this" screen only advertises that it exists. The
 * layout is not rendered at all in the meantime, which is also what keeps its
 * queries from firing and coming back 403.
 *
 * The API refuses these routes independently. This is only about what shows.
 */
function GapReviewRoute() {
  const { isLoaded, isInternalUser } = useInternalAccess();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoaded && !isInternalUser) navigate({ to: '/', replace: true });
  }, [isLoaded, isInternalUser, navigate]);

  if (!isLoaded || !isInternalUser) return null;

  return <GapReviewLayout />;
}

/**
 * The gap-review shell: theme root, topbar, and the two queries every view below
 * reads — the runs, and the selected run's drafts.
 *
 * The split is what makes the surface open quickly. A draft travels whole, both
 * gap layers of it, so fetching every run's drafts to draw a picker meant paying
 * for the entire corpus to look at one run. The runs come back with their tallies
 * counted in the database, and only the selected run's gaps are fetched.
 *
 * `/gap-review` is registered as a chromeless path in `__root.tsx`, so this is
 * the whole window — no app nav, no sidebar. That is deliberate: the surface is
 * an internal review tool with its own three-column layout and its own keyboard
 * map, and the app chrome would compete with both.
 *
 * This page is English-only and not translated, unlike the rest of the app: it
 * is a developer tool whose vocabulary is the pipeline's own field names.
 */
function GapReviewLayout() {
  const { theme, toggleTheme } = useGapReviewTheme();

  const {
    data: executionsData,
    isPending: areExecutionsPending,
    error: executionsError,
  } = useGapExecutions();
  const executions = executionsData?.executions;

  const { execution: searchExecutionId } = Route.useSearch();
  // `strict: false` because the layout renders above routes with and without
  // params; only the run audit has an `executionId`, and while the reviewer is
  // on it that run IS the selection — otherwise auditing one run would show
  // another one's gaps behind it.
  const { executionId: auditedExecutionId } = useParams({ strict: false });

  // Auto-select, when the URL names no run: the most recent one, which is the
  // work a reviewer opening this tool almost always means. The queue writes that
  // choice back into the address bar — see its own effect — so a link copied off
  // it names the run rather than meaning "whichever is newest today".
  const selectedExecutionId = resolveSelectedRun({
    auditedExecutionId,
    searchExecutionId,
    executions,
  });

  const {
    data: draftsData,
    isPending: areDraftsPending,
    error: draftsError,
  } = useGapDrafts(selectedExecutionId);
  const drafts = draftsData?.drafts;

  // The keyboard map belongs to the queue, so the legend follows the route
  // rather than the data. The trailing slash is stripped because the index route
  // is reachable both ways.
  const pathname = useRouterState({
    select: (routerState) => routerState.location.pathname.replace(/\/$/, ''),
  });
  const isQueueView = pathname === '/gap-review';

  // Over the selected run: the queue holds one run, so a corpus-wide number
  // beside it would name work the reviewer cannot see.
  const pendingCount =
    drafts?.filter(
      (draft) => draft.review.status === GapDraftReviewStatus.PENDING
    ).length ?? 0;
  // Over every run, because that is what the page behind it lists — the kept
  // list is the one view here that is not about a run. See `countKeptAcrossRuns`.
  const keptCount = countKeptAcrossRuns({
    executions: executions ?? [],
    selectedExecutionId: selectedExecutionId,
    selectedRunDrafts: drafts,
  });

  const isPending =
    areExecutionsPending ||
    (selectedExecutionId !== undefined && areDraftsPending);
  const error = executionsError ?? draftsError;

  return (
    <PlatformRestriction notAllowedPlatforms={['mobile']}>
      <div className="gr-root" data-theme={theme}>
        <GapReviewTopbar
          pendingCount={pendingCount}
          keptCount={keptCount}
          executionId={selectedExecutionId}
          showKeyboardHint={drafts !== undefined && isQueueView}
          theme={theme}
          onToggleTheme={toggleTheme}
        />

        {isPending && (
          <div className="gr-view gr-page">
            <div className="gr-page-in">
              <div className="gr-empty">
                <b>Loading</b>
                {areExecutionsPending
                  ? 'Fetching the runs.'
                  : "Fetching this run's gaps."}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="gr-view gr-page">
            <div className="gr-page-in">
              <div className="gr-empty">
                <b>Could not load the gap review</b>
                {error.message}
              </div>
            </div>
          </div>
        )}

        {/* No run to select and nothing to fetch — said here rather than left to
            each view, which would otherwise each render an empty version of
            itself against a company that has never run the pipeline. */}
        {!isPending && !error && selectedExecutionId === undefined && (
          <div className="gr-view gr-page">
            <div className="gr-page-in">
              <div className="gr-empty">
                <b>No runs</b>
                No gap-analysis execution has been performed for this company
                yet.
              </div>
            </div>
          </div>
        )}

        {executions !== undefined &&
          drafts !== undefined &&
          selectedExecutionId !== undefined && (
            <GapReviewDataProvider
              value={{
                executions,
                drafts,
                executionId: selectedExecutionId,
              }}
            >
              <Outlet />
            </GapReviewDataProvider>
          )}
      </div>
    </PlatformRestriction>
  );
}
